import React, { useEffect, useRef, useState } from 'react';
import {
  Figure,
  MainImageWrapper,
  SwiperContainer,
  ThumbImageWrapper,
  ThumbLink,
  VideoContainer,
} from './MediaThumbGallery.styles';
import Image from '@/providers/Image';
import { SwiperSlide } from 'swiper/react';
import dynamic from 'next/dynamic';
import useImagePulse from '@/components/media/imagePulseHook';
import { mediaBlurDataUrl, mediaUrlCdn } from '@/utils/media';

const MediaSlidesGallery = dynamic(import('@/components/media/MediaSlidesGallery/MediaSlidesGallery'));

export interface ImageGalleryProps {
  media: [MediaObjType];
}

const MediaThumbGallery = ({ media }: ImageGalleryProps) => {
  const images = media;

  const [open, setOpen] = useState(false);
  const [imageFeatured, setImageFeatured] = useState<MediaObjType>(images[0] as MediaObjType);
  const imageRef = useRef<HTMLImageElement>(null);

  const { className, containerRef, handleOnCompleted } = useImagePulse();

  const toggle = () => {
    setOpen((prevState) => !prevState);
  };

  const loadFeaturedImage = (e: React.MouseEvent<HTMLAnchorElement>, image: MediaObjType) => {
    e.preventDefault();
    if (imageFeatured.image_url === image.image_url) {
      return;
    }

    if (imageRef.current) {
      containerRef.current?.classList.add('img-pulsing');
      imageRef.current.classList.add('img-pending');
      imageRef.current.src = `data:image/jpeg;base64,${image.blur_data_url}`;
      imageRef.current.classList.remove('img-loaded');
      imageRef.current.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    }
    setImageFeatured(() => image);
  };

  useEffect(() => {
    imageRef?.current?.classList.remove('img-loaded');
    if (images.length > 0) {
      setImageFeatured(() => images[0]);
    }
  }, [images, imageRef]);

  if (!images.length) {
    return null;
  }

  if (!imageFeatured) {
    return null;
  }

  return (
    <Figure id="featured-image">
      {(imageFeatured?.resource_type === 'video' && (
        <VideoContainer>
          <iframe src={`https://www.youtube.com/embed/${imageFeatured.filename}?autoplay=0`} />
        </VideoContainer>
      )) || (
        <MainImageWrapper
          href={`#${imageFeatured.filename?.replace(/\.jpg/, '')}`}
          onClick={toggle}
          ref={containerRef as React.RefObject<HTMLAnchorElement>}
          className={className}
        >
          <Image
            ref={imageRef}
            src={mediaUrlCdn({ media: imageFeatured })}
            alt={imageFeatured.title}
            fill
            placeholder={mediaBlurDataUrl({ media: imageFeatured })}
            fadeInOnBlur
            handleOnCompleted={handleOnCompleted}
          />
        </MainImageWrapper>
      )}

      <figcaption>
        <SwiperContainer
          mousewheel={true}
          grabCursor={true}
          breakpoints={{
            0: {
              slidesPerView: 5,
              spaceBetween: 16,
            },
            500: {
              slidesPerView: 10,
              spaceBetween: 0,
            },
          }}
        >
          {images.map((media, index) => (
            <SwiperSlide key={`${media.image_url}-${index}`}>
              <ThumbLink href="#featured-image" onClick={(e) => loadFeaturedImage(e, media)}>
                <ThumbImageWrapper
                  active={media.image_url === imageFeatured.image_url}
                  contentType={media.resource_type}
                >
                  <Image
                    src={mediaUrlCdn({ media, options: { thumbnail: true } })}
                    alt={media.title}
                    fill
                    placeholder={mediaBlurDataUrl({ media })}
                    fadeInOnBlur
                  />
                </ThumbImageWrapper>
              </ThumbLink>
            </SwiperSlide>
          ))}
        </SwiperContainer>
      </figcaption>
      {open && <MediaSlidesGallery images={images} open={open} toggle={toggle} />}
    </Figure>
  );
};

export default MediaThumbGallery;
